function keyOf(x, y, z) {
  return `${x},${y},${z}`;
}

function parseKey(key) {
  const [x, y, z] = String(key).split(',').map(Number);
  return { x, y, z };
}

export function createRedstoneSystem({
  world,
  spawnItemDrop
}) {
  const LEVER_OFF = 58;
  const LEVER_ON = 59;
  const WIRE_OFF = 60;
  const WIRE_ON = 61;
  const LAMP_OFF = 62;
  const LAMP_ON = 63;

  const REDSTONE_IDS = new Set([LEVER_OFF, LEVER_ON, WIRE_OFF, WIRE_ON, LAMP_OFF, LAMP_ON]);
  const SUPPORT_IDS = new Set([LEVER_OFF, LEVER_ON, WIRE_OFF, WIRE_ON]);
  const neighborDirs = [
    [1, 0, 0],
    [-1, 0, 0],
    [0, 0, 1],
    [0, 0, -1],
    [0, 1, 0],
    [0, -1, 0]
  ];

  function isRedstoneBlockId(id) {
    return REDSTONE_IDS.has(Number(id));
  }

  function isSupportRequired(id) {
    return SUPPORT_IDS.has(Number(id));
  }

  function toDropItemId(blockId) {
    const id = Number(blockId);
    if (id === LEVER_ON) return String(LEVER_OFF);
    if (id === WIRE_ON) return String(WIRE_OFF);
    if (id === LAMP_ON) return String(LAMP_OFF);
    return String(id);
  }

  function setBlockState(x, y, z, nextId) {
    const currentId = Number(world.getBlockIdAt(x, y, z));
    if (currentId === Number(nextId)) return true;
    const block = world.getBlockAt(x, y, z);
    if (block) world.removeBlock(block, { trackModification: true });
    const next = world.createBlock(x, y, z, nextId);
    if (next) return true;
    if (currentId) world.createBlock(x, y, z, currentId);
    return false;
  }

  function collectAround(centerX, centerY, centerZ, radiusXZ = 12, rangeY = 5) {
    const wires = [];
    const lamps = [];
    const sources = [];

    const minX = centerX - radiusXZ;
    const maxX = centerX + radiusXZ;
    const minY = centerY - rangeY;
    const maxY = centerY + rangeY;
    const minZ = centerZ - radiusXZ;
    const maxZ = centerZ + radiusXZ;

    for (let z = minZ; z <= maxZ; z++) {
      for (let y = minY; y <= maxY; y++) {
        for (let x = minX; x <= maxX; x++) {
          const id = Number(world.getBlockIdAt(x, y, z));
          if (id === LEVER_ON) {
            sources.push({ x, y, z });
            continue;
          }
          if (id === WIRE_OFF || id === WIRE_ON) {
            wires.push({ x, y, z });
            continue;
          }
          if (id === LAMP_OFF || id === LAMP_ON) {
            lamps.push({ x, y, z });
          }
        }
      }
    }

    return { wires, lamps, sources };
  }

  function computePoweredWires(sources) {
    const powered = new Set();
    const queue = [];
    const visitedNodes = new Set();

    for (const source of sources) {
      queue.push(source);
      visitedNodes.add(keyOf(source.x, source.y, source.z));
    }

    while (queue.length > 0) {
      const cur = queue.shift();
      for (const [dx, dy, dz] of neighborDirs) {
        const nx = cur.x + dx;
        const ny = cur.y + dy;
        const nz = cur.z + dz;
        const neighborId = Number(world.getBlockIdAt(nx, ny, nz));
        if (neighborId !== WIRE_OFF && neighborId !== WIRE_ON) continue;
        const wireKey = keyOf(nx, ny, nz);
        if (powered.has(wireKey)) continue;
        powered.add(wireKey);
        if (visitedNodes.has(wireKey)) continue;
        visitedNodes.add(wireKey);
        queue.push({ x: nx, y: ny, z: nz });
      }
    }

    return powered;
  }

  function hasPoweredNeighbor(x, y, z) {
    for (const [dx, dy, dz] of neighborDirs) {
      const id = Number(world.getBlockIdAt(x + dx, y + dy, z + dz));
      if (id === LEVER_ON || id === WIRE_ON) return true;
    }
    return false;
  }

  function recomputeNetworkAround(x, y, z) {
    const centerX = Math.round(x);
    const centerY = Math.round(y);
    const centerZ = Math.round(z);

    const { wires, lamps, sources } = collectAround(centerX, centerY, centerZ);
    const poweredWires = computePoweredWires(sources);

    for (const wire of wires) {
      const k = keyOf(wire.x, wire.y, wire.z);
      const shouldOn = poweredWires.has(k);
      const nextId = shouldOn ? WIRE_ON : WIRE_OFF;
      setBlockState(wire.x, wire.y, wire.z, nextId);
    }

    for (const lamp of lamps) {
      const shouldOn = hasPoweredNeighbor(lamp.x, lamp.y, lamp.z);
      const nextId = shouldOn ? LAMP_ON : LAMP_OFF;
      setBlockState(lamp.x, lamp.y, lamp.z, nextId);
    }
  }

  function removeWithDrop(x, y, z) {
    const block = world.getBlockAt(x, y, z);
    if (!block) return false;
    const id = Number(block.userData?.id);
    world.removeBlock(block, { trackModification: true });
    if (typeof spawnItemDrop === 'function') {
      spawnItemDrop(x, y, z, toDropItemId(id), 1);
    }
    return true;
  }

  function cleanupUnsupportedAround(x, y, z) {
    let changed = false;
    for (let bz = z - 1; bz <= z + 1; bz++) {
      for (let by = y - 1; by <= y + 2; by++) {
        for (let bx = x - 1; bx <= x + 1; bx++) {
          const id = Number(world.getBlockIdAt(bx, by, bz));
          if (!isSupportRequired(id)) continue;
          if (world.isSolidAt(bx, by - 1, bz)) continue;
          if (removeWithDrop(bx, by, bz)) changed = true;
        }
      }
    }
    return changed;
  }

  function tryToggleLever(block) {
    if (!block) return false;
    const id = Number(block.userData?.id);
    if (id !== LEVER_OFF && id !== LEVER_ON) return false;

    const x = Math.round(block.position.x);
    const y = Math.round(block.position.y);
    const z = Math.round(block.position.z);
    const nextId = id === LEVER_OFF ? LEVER_ON : LEVER_OFF;
    if (!setBlockState(x, y, z, nextId)) return false;
    recomputeNetworkAround(x, y, z);
    return true;
  }

  function onBlockPlaced(x, y, z, blockId) {
    if (!isRedstoneBlockId(blockId)) return;
    recomputeNetworkAround(x, y, z);
  }

  function onBlockRemoved(x, y, z, blockId = null) {
    const id = Number(blockId);
    if (isRedstoneBlockId(id) || isSupportRequired(id)) {
      const changed = cleanupUnsupportedAround(x, y, z);
      recomputeNetworkAround(x, y, z);
      if (changed) recomputeNetworkAround(x, y, z);
      return;
    }

    if (cleanupUnsupportedAround(x, y, z)) {
      recomputeNetworkAround(x, y, z);
    }
  }

  return {
    isRedstoneBlockId,
    isSupportRequired,
    tryToggleLever,
    onBlockPlaced,
    onBlockRemoved,
    recomputeNetworkAround,
    cleanupUnsupportedAround,
    toDropItemId,
    parseKey
  };
}
